import React, { FC, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';

import { useHandleBorrowClick } from '../hooks/useHandleBorrowClick';
import { useBorrowProgress } from '../hooks/useBorrowProgress';
import { useGetAgreement } from '../hooks/useGetAgreement';

import { Agreement, ROLE } from '@/shared/api/borrow';
import { formatSmart, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { Store } from '@/shared/store';
import { ConnectWalletWrapper } from '@/widgets/ConnectWalletWrapper';
import { AssetWithIcon, Button, ButtonSize, ButtonVariant, Progress, Skeleton } from '@/shared/ui';
import { useRefreshAgreements } from '@/shared/hooks/useRefreshAgreements';

type AssetsToBorrowRowsProps = {
    allItems: Agreement[];
    itemIndex: number;
    role: ROLE;
};

export const AssetsToBorrowItemRow: FC<AssetsToBorrowRowsProps> = ({
    itemIndex,
    allItems,
    role,
}) => {
    const navigate = useNavigate();

    const {
        address,
        title,
        leverage,
        apy,
        totalDeposited,
        totalDepositThreshold,
        noWalletInfo,
        errorContract,
    } = useGetAgreement(allItems, itemIndex);

    const walletAddress = useSelector((state: Store) => state.wallets.active.address);

    const refreshAgreements = useRefreshAgreements(role);

    const handleBorrowClick = useHandleBorrowClick(leverage, address, refreshAgreements);

    const borrowProgress = useBorrowProgress(totalDeposited!, totalDepositThreshold!);

    const handleDetails = () => navigate(`/pool/${address}?role=borrower`);

    const borrowBalance = useMemo(() => {
        if (!leverage?.userBalance) {
            return 'N/A';
        }

        return formatSmart(
            formatWithMaxPrecision(leverage?.userBalance ?? '0', leverage?.decimals),
        );
    }, [leverage?.userBalance, leverage?.decimals]);

    const borrowTotalDeposited = useMemo(() => {
        return formatSmart(
            formatWithMaxPrecision(totalDepositThreshold ?? '0', leverage?.decimals),
        );
    }, [totalDepositThreshold, leverage?.decimals]);

    return (
        <tr
            className='h-[72px] border-t border-gray-300 bg-white hover:bg-gray-100 [&_td]:font-semibold'
            data-testid={`borrow-row-${title}-${itemIndex}`}
        >
            <td className='pl-6'>{title}</td>
            <td>
                {leverage?.symbol ? (
                    <AssetWithIcon
                        assetName={leverage?.symbol}
                        className='[&_img]:h-[24px] [&_img]:w-[24px] [&_div]:text-[14px]'
                    />
                ) : (
                    <Skeleton />
                )}
            </td>
            <td>
                <div className='mt-2 mr-8'>
                    {borrowTotalDeposited !== '0' && !noWalletInfo ? (
                        <>
                            <div
                                data-testid={`borrow-total-deposited-${itemIndex}`}
                            >{`${borrowTotalDeposited} ${leverage?.symbol || ''}`}</div>
                            <Progress percent={borrowProgress} />
                        </>
                    ) : (
                        <span className='flex'>
                            <Skeleton className='mr-4' />
                            {leverage?.symbol || ''}
                        </span>
                    )}
                </div>
            </td>
            <td>
                <div data-testid={`borrow-apy-${itemIndex}`}>{apy}%</div>
            </td>
            <td>
                {borrowBalance !== 'N/A' && !noWalletInfo && borrowBalance !== 'NaN' ? (
                    <div data-testid={`borrow-balance-${itemIndex}`}>
                        {`${borrowBalance}`}{' '}
                        <span className='text-content-secondary font-normal'>
                            {leverage?.symbol}
                        </span>
                    </div>
                ) : (
                    <Skeleton />
                )}
            </td>
            <td>
                <div className='flex justify-end gap-5 mr-6'>
                    <ConnectWalletWrapper noWallet={noWalletInfo}>
                        <Button
                            variant={ButtonVariant.Primary}
                            size={ButtonSize.Medium}
                            name={`borrow-asset-${itemIndex}`}
                            className='w-24 border-none'
                            onClick={handleBorrowClick}
                            disabled={!walletAddress || errorContract || !leverage}
                        >
                            Borrow
                        </Button>
                    </ConnectWalletWrapper>

                    <Button
                        variant={ButtonVariant.Secondary}
                        size={ButtonSize.Medium}
                        className='w-24'
                        onClick={handleDetails}
                        name={`borrow-details-${itemIndex}`}
                        disabled={noWalletInfo || errorContract || !leverage}
                    >
                        Details
                    </Button>
                </div>
            </td>
        </tr>
    );
};
